import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Plus, PackageOpen, Undo2, Search } from 'lucide-react'
import { FeaturedLayout } from '../../layouts'
import {
  Button, Card, CardContent, CardHeader, CardTitle, Badge, Input,
} from '../../ui'
import {
  getPrestamosActivos, devolverPrestamo, getAllPlayersWithGroups,
} from '../../../lib/supabase'
import { useConfirmDialog } from '../../../hooks/useConfirmDialog'
import PrestamoFormModal from './PrestamoFormModal'

const TIPO_LABELS = {
  prestamo: 'Préstamo',
  asignacion: 'Asignación',
}

const InventarioPorJugador = () => {
  const { confirm, alert, ConfirmDialog } = useConfirmDialog()
  const [loans, setLoans] = useState([])
  const [players, setPlayers] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [formOpen, setFormOpen] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    const [loansRes, playersRes] = await Promise.all([
      getPrestamosActivos(),
      getAllPlayersWithGroups(),
    ])
    setLoans((loansRes.data || []).filter((l) => l.prestado_a_tipo === 'jugador'))
    setPlayers(playersRes.data || [])
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  // Agrupa los prestamos activos por jugador. Solo aparecen jugadores con equipo.
  const grupos = useMemo(() => {
    const byId = Object.fromEntries(players.map((p) => [p.id, p.nombre_completo]))
    const map = new Map()
    loans.forEach((loan) => {
      const key = loan.prestado_a_id
      if (!map.has(key)) {
        map.set(key, { id: key, nombre: byId[key] || 'Jugador', items: [] })
      }
      map.get(key).items.push(loan)
    })
    const arr = Array.from(map.values())
    arr.sort((a, b) => a.nombre.localeCompare(b.nombre))
    return arr
  }, [loans, players])

  const gruposFiltrados = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return grupos
    return grupos.filter((g) => g.nombre.toLowerCase().includes(term))
  }, [grupos, search])

  const handleDevolver = async (loan, jugadorNombre) => {
    const nombre = loan.item?.nombre || 'el artículo'
    const ok = await confirm(
      'Registrar devolución',
      `¿Marcar como devuelto "${nombre}" (${loan.cantidad}) de ${jugadorNombre}?`,
      { confirmText: 'Devolver' }
    )
    if (!ok) return
    const { error } = await devolverPrestamo(loan.id)
    if (error) {
      await alert('Error', error.message || 'No se pudo registrar la devolución')
      return
    }
    load()
  }

  return (
    <FeaturedLayout
      title="Equipo por jugador"
      subtitle="Qué equipo tiene asignado cada jugador y si lo devolvió"
      refreshAction={load}
    >
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="relative sm:max-w-xs w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Buscar jugador..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Button onClick={() => setFormOpen(true)}>
          <Plus className="h-4 w-4 mr-2" /> Asignar equipo
        </Button>
      </div>

      {loading ? (
        <p className="text-muted-foreground text-sm py-8 text-center">Cargando...</p>
      ) : gruposFiltrados.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <PackageOpen className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
            <p className="text-muted-foreground mb-4">
              {grupos.length === 0
                ? 'Ningún jugador tiene equipo asignado'
                : 'Ningún jugador coincide con la búsqueda'}
            </p>
            {grupos.length === 0 && (
              <Button onClick={() => setFormOpen(true)}>
                <Plus className="h-4 w-4 mr-2" /> Asignar equipo
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {gruposFiltrados.map((g) => (
            <Card key={g.id}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between gap-2">
                  <span>{g.nombre}</span>
                  <Badge variant="secondary" className="text-xs">{g.items.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <ul className="divide-y">
                  {g.items.map((loan) => {
                    const detalle = [loan.item?.talla, loan.item?.modelo].filter(Boolean).join(' · ')
                    return (
                      <li key={loan.id} className="flex items-center justify-between gap-3 py-2">
                        <div className="min-w-0">
                          <span className="text-sm font-medium">{loan.item?.nombre || 'Artículo'}</span>
                          {loan.cantidad > 1 && (
                            <span className="text-xs text-muted-foreground"> ×{loan.cantidad}</span>
                          )}
                          <p className="text-xs text-muted-foreground">
                            {[detalle, TIPO_LABELS[loan.tipo] || loan.tipo, loan.fecha_prestamo]
                              .filter(Boolean)
                              .join(' · ')}
                          </p>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => handleDevolver(loan, g.nombre)}>
                          <Undo2 className="h-3.5 w-3.5 sm:mr-1" />
                          <span className="hidden sm:inline">Devolver</span>
                        </Button>
                      </li>
                    )
                  })}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <PrestamoFormModal
        open={formOpen}
        onOpenChange={setFormOpen}
        onSuccess={load}
        title="Asignar equipo a jugador"
        defaultValues={{ tipo: 'asignacion', prestado_a_tipo: 'jugador' }}
      />

      <ConfirmDialog />
    </FeaturedLayout>
  )
}

export default InventarioPorJugador
